/**
 * SitePreviewRenderer — Renders a live site preview from SandboxDb data.
 *
 * Reads sections, branding, and nav items from the sandbox database
 * and renders a realistic light-themed site preview inside browser chrome.
 */

import { useState, useEffect, useCallback } from 'react';
import type { SandboxDb } from '../../lib/sandbox-db';
import type { SiteMeta, Section, NavItem, Branding, SectionType } from '../../lib/site-schema';
import HeroSection from './sections/HeroSection';
import FeaturesSection from './sections/FeaturesSection';
import TestimonialsSection from './sections/TestimonialsSection';
import MenuSection from './sections/MenuSection';
import GallerySection from './sections/GallerySection';
import ContactSection from './sections/ContactSection';
import CTASection from './sections/CTASection';
import HoursSection from './sections/HoursSection';

interface Props {
  sandboxDb: SandboxDb | null;
  viewport?: 'mobile' | 'desktop' | 'wide';
  size?: 'compact' | 'large';
  siteUrl?: string;
  onClaimClick?: () => void;
  tier?: 'sandbox' | 'free' | 'pro';
}

export default function SitePreviewRenderer({
  sandboxDb,
  size = 'compact',
  siteUrl,
  onClaimClick,
  tier,
}: Props) {
  const [siteMeta, setSiteMeta] = useState<SiteMeta | null>(null);
  const [sections, setSections] = useState<Section[]>([]);
  const [navItems, setNavItems] = useState<NavItem[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [activeNavIndex, setActiveNavIndex] = useState(0);

  const isLarge = size === 'large';
  const compact = !isLarge;

  const loadData = useCallback(async () => {
    if (!sandboxDb?.ready) return;
    try {
      const [meta, sects, nav] = await Promise.all([
        sandboxDb.getSiteMeta(),
        sandboxDb.getHomeSections(),
        sandboxDb.getNavItems(),
      ]);
      setSiteMeta(meta);
      setSections(sects);
      setNavItems(nav);
    } catch (e) {
      console.error('SitePreviewRenderer: failed to load data', e);
    }
  }, [sandboxDb]);

  useEffect(() => { loadData(); }, [loadData, refreshKey]);

  useEffect(() => {
    if (!sandboxDb) return;
    const unsub = sandboxDb.onDataChange(() => setRefreshKey((k) => k + 1));
    return unsub;
  }, [sandboxDb]);

  const branding = (
    typeof siteMeta?.branding === 'string'
      ? JSON.parse(siteMeta.branding as string)
      : siteMeta?.branding
  ) as Branding | null;

  const siteName = siteMeta?.name || 'Your Site';
  const url = siteUrl || 'your-site.wiserwebhost.com';
  const primary = branding?.primaryColor || '#d97706';

  const renderSection = (section: Section) => {
    const content = typeof section.content === 'string'
      ? JSON.parse(section.content as string)
      : section.content;
    const sectionProps = { branding, compact };

    switch (section.type as SectionType) {
      case 'hero':
        return <HeroSection key={section.id} content={content} siteName={siteName} {...sectionProps} />;
      case 'features':
        return <FeaturesSection key={section.id} content={content} {...sectionProps} />;
      case 'testimonials':
        return <TestimonialsSection key={section.id} content={content} {...sectionProps} />;
      case 'menu':
        return <MenuSection key={section.id} content={content} {...sectionProps} />;
      case 'gallery':
        return <GallerySection key={section.id} content={content} {...sectionProps} />;
      case 'contact':
        return <ContactSection key={section.id} content={content} {...sectionProps} />;
      case 'cta':
        return <CTASection key={section.id} content={content} {...sectionProps} />;
      case 'hours':
        return <HoursSection key={section.id} content={content} {...sectionProps} />;
      default:
        return null;
    }
  };

  // ─── Empty state ───────────────────────────────────────────────────────────
  if (!siteMeta && sections.length === 0) {
    return (
      <div className="rounded-xl overflow-hidden shadow-2xl shadow-black/40 border border-zinc-700/50">
        {/* Chrome bar */}
        <div className="flex items-center gap-3 px-4 py-2.5 bg-zinc-800 border-b border-zinc-700/50">
          <div className="flex gap-2">
            <span className="w-3 h-3 rounded-full bg-[#ff5f57] shadow-[0_0_4px_#ff5f5740]" />
            <span className="w-3 h-3 rounded-full bg-[#febc2e] shadow-[0_0_4px_#febc2e40]" />
            <span className="w-3 h-3 rounded-full bg-[#28c840] shadow-[0_0_4px_#28c84040]" />
          </div>
          <div className="flex-1 flex items-center gap-2 bg-zinc-900/80 rounded-lg px-3 py-1.5">
            <svg className="w-3 h-3 text-green-500 shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" /></svg>
            <span className="text-[11px] text-zinc-500 font-mono tracking-wide truncate">{url}</span>
          </div>
        </div>
        {/* Empty placeholder */}
        <div className="bg-gradient-to-br from-stone-50 to-stone-100" style={{ minHeight: compact ? 140 : 220 }}>
          <div className="flex items-center justify-center h-full" style={{ minHeight: compact ? 140 : 220 }}>
            <div className="text-center opacity-40">
              <svg className="w-10 h-10 mx-auto mb-3 text-stone-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" /></svg>
              <p className="text-xs text-stone-400 font-medium">Your site will appear here</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── Live preview ──────────────────────────────────────────────────────────
  return (
    <div className="rounded-xl overflow-hidden shadow-2xl shadow-black/40 border border-zinc-700/50 relative">
      {/* Sandbox claim badge */}
      {tier === 'sandbox' && onClaimClick && (
        <button
          onClick={onClaimClick}
          className="absolute top-[46px] right-3 z-20 px-2.5 py-1 bg-amber-500 text-white text-[10px] font-semibold rounded-md shadow-lg shadow-amber-500/20 hover:bg-amber-400 hover:shadow-amber-400/30 transition-all flex items-center gap-1.5 tracking-wide"
        >
          <span className="material-icons text-xs">hourglass_empty</span>
          Sandbox — Claim to keep
        </button>
      )}

      {/* Browser chrome */}
      <div className="flex items-center gap-3 px-4 py-2.5 bg-zinc-800 border-b border-zinc-700/50">
        <div className="flex gap-2">
          <span className="w-3 h-3 rounded-full bg-[#ff5f57] shadow-[0_0_4px_#ff5f5740]" />
          <span className="w-3 h-3 rounded-full bg-[#febc2e] shadow-[0_0_4px_#febc2e40]" />
          <span className="w-3 h-3 rounded-full bg-[#28c840] shadow-[0_0_4px_#28c84040]" />
        </div>
        <div className="flex-1 flex items-center gap-2 bg-zinc-900/80 rounded-lg px-3 py-1.5">
          <svg className="w-3 h-3 text-green-500 shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" /></svg>
          <span className="text-[11px] text-zinc-500 font-mono tracking-wide truncate">{url}</span>
        </div>
        {isLarge && (
          <a href="#" className="text-[11px] text-zinc-400 hover:text-white flex items-center gap-1 transition shrink-0 font-medium">
            <span className="material-icons text-sm">open_in_new</span>
          </a>
        )}
      </div>

      {/* Site navigation */}
      {navItems.length > 0 && (
        <div
          className={`flex items-center ${compact ? 'px-4 py-2' : 'px-6 py-3'} border-b`}
          style={{
            background: 'linear-gradient(180deg, #fafaf9, #f5f5f4)',
            borderColor: '#e7e5e4',
          }}
        >
          <span
            className={`${compact ? 'text-xs' : 'text-sm'} font-bold tracking-tight mr-4`}
            style={{ color: primary, fontFamily: branding?.fontHeading }}
          >
            {siteName}
          </span>
          <div className="flex items-center gap-0.5 flex-1 overflow-x-auto">
            {navItems.map((item, idx) => (
              <button
                key={item.id}
                onClick={() => setActiveNavIndex(idx)}
                className={`${compact ? 'text-[10px] px-2 py-0.5' : 'text-[11px] px-3 py-1'} rounded-md font-medium transition-all whitespace-nowrap ${
                  item.is_cta ? '' : ''
                }`}
                style={
                  item.is_cta
                    ? {
                        background: primary,
                        color: '#fff',
                        fontSize: compact ? '10px' : '11px',
                        boxShadow: `0 2px 8px ${primary}40`,
                      }
                    : {
                        color: activeNavIndex === idx ? primary : '#78716c',
                        background: activeNavIndex === idx ? `${primary}08` : 'transparent',
                      }
                }
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Page content — light themed */}
      <div
        className="overflow-y-auto"
        style={{
          background: 'linear-gradient(180deg, #fafaf9 0%, #f5f5f0 100%)',
          maxHeight: isLarge ? 'none' : '65vh',
        }}
      >
        {sections.map(renderSection)}

        {/* Footer */}
        <div
          className={`${compact ? 'px-4 py-3' : 'px-6 py-5'} border-t text-center`}
          style={{ borderColor: '#e7e5e4' }}
        >
          <p className={`${compact ? 'text-[9px]' : 'text-[11px]'} font-medium`} style={{ color: '#a8a29e' }}>
            &copy; {new Date().getFullYear()} {siteName}. All rights reserved.
          </p>
          <p className={`${compact ? 'text-[8px]' : 'text-[10px]'} mt-0.5`} style={{ color: '#d6d3d1' }}>
            Powered by WiserWeb
          </p>
        </div>
      </div>
    </div>
  );
}
